// src/pages/CategoryPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import products from '../data/products';
import './categoryPage.css';

const CategoryPage = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [selectedStrain, setSelectedStrain] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [isLoading, setIsLoading] = useState(false);
  
  const categoryProducts = products.filter(p => p.category === category);
  const strains = ['all', ...new Set(categoryProducts.map(p => p.strain))];
  
  // Simulate loading
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [selectedStrain, sortBy, category]);

  const filteredProducts = selectedStrain === 'all' 
    ? categoryProducts 
    : categoryProducts.filter(p => p.strain === selectedStrain);

  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'name-asc':
        return a.name.localeCompare(b.name);
      case 'name-desc':
        return b.name.localeCompare(a.name);
      case 'newest':
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      default:
        return a.isFeatured ? -1 : 1;
    }
  });

  const getCategoryName = (cat) => {
    return cat.replace('-', ' ');
  };

  const getCategoryDescription = (cat) => {
    const descriptions = {
      'weekly-special': 'Our Weekly specials.',
      'Flower': 'Premium cannabis flower available in various strains and quantities. Hand-selected for quality and potency.',
      'Exotic-Flower': 'Top-shelf exotic strains for the discerning connoisseur. Rare genetics, exceptional terpene profiles.',
      'Edibles': 'Delicious THC-infused edibles in various forms and potencies. Perfect for precise dosing.',
      'Concentrates': 'High-potency concentrates for experienced users. Extracted using premium methods.',
      'cart': '510-thread vape cartridges with premium extracts. Discreet and convenient consumption.',
      'disposable-cart': 'Convenient disposable vape pens in various strains. Ready to use, no charging required.',
      'Pre-rolls': 'Pre-rolled joints for instant enjoyment. Perfectly rolled for optimal smoking experience.',
      'Shake': 'Premium shake at great value. Ideal for cooking, rolling, or vaporizing.'
    };
    return descriptions[cat] || 'Premium cannabis products.';
  };

  const getCategoryIcon = (cat) => {
    const icons = {
      'weekly-special': '🏷️',
      'Flower': '🌿',
      'Exotic-Flower': '🌸',
      'Edibles': '🍬',
      'Concentrates': '⚗️',
      'cart': '🔋',
      'disposable-cart': '💨',
      'Pre-rolls': '🚬',
      'Shake': '🍃'
    };
    return icons[cat] || '📦';
  };

  const getCategoryColor = (cat) => {
    const colors = {
      'weekly-special': '#ef1325',
      'Flower': '#10b981',
      'Exotic-Flower': '#8b5cf6',
      'Edibles': '#f59e0b',
      'Concentrates': '#3b82f6',
      'cart': '#06b6d4',
      'disposable-cart': '#6366f1',
      'Pre-rolls': '#ef4444',
      'Shake': '#78716c'
    };
    return colors[cat] || '#6b7280';
  };

  const resetFilters = () => {
    setSelectedStrain('all');
    setSortBy('featured');
  };

  return (
    <div className="category-page">
      {/* Category Header */}
      <div className="category-header" style={{ '--category-color': getCategoryColor(category) }}>
        <div className="category-header-content">
          <nav className="breadcrumb">
            <Link to="/categories" className="breadcrumb-link">
              <svg viewBox="0 0 24 24" fill="none" className="breadcrumb-icon">
                <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Back to Categories
            </Link>
          </nav>
          
          <div className="category-info">
            <div className="category-icon-large">{getCategoryIcon(category)}</div>
            <div className="category-text">
              <h1 className="category-title">{getCategoryName(category)}</h1>
              <p className="category-description">{getCategoryDescription(category)}</p>
              <div className="category-stats">
                <span className="stat-item">
                  <svg viewBox="0 0 24 24" fill="none" className="stat-icon">
                    <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" 
                      stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1h1a1 1 0 001-1v-3h1a2 2 0 012 2v2a1 1 0 001 1h1" 
                      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  {sortedProducts.length} Products
                </span>
                <span className="stat-item">
                  <svg viewBox="0 0 24 24" fill="none" className="stat-icon">
                    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  Fast Delivery
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Controls */}
      <div className="category-container">
        <div className="category-controls">
          {/* Strain Filter */}
          {strains.length > 2 && (
            <div className="control-section">
              <label className="control-label">
                <svg viewBox="0 0 24 24" fill="none" className="control-icon">
                  <path d="M9.663 17h4.674M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" 
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                Filter by Strain
              </label>
              <div className="strain-filter">
                <button
                  onClick={() => setSelectedStrain('all')}
                  className={`strain-btn ${selectedStrain === 'all' ? 'active' : ''}`}
                >
                  All Strains
                  <span className="strain-count">{categoryProducts.length}</span>
                </button>
                {strains
                  .filter(strain => strain !== 'all')
                  .map(strain => {
                    const count = categoryProducts.filter(p => p.strain === strain).length;
                    const getStrainColor = (s) => {
                      if (s === 'Sativa') return 'bg-yellow-100 text-yellow-800';
                      if (s === 'Indica') return 'bg-purple-100 text-purple-800';
                      if (s === 'Hybrid') return 'bg-green-100 text-green-800';
                      return 'bg-gray-100 text-gray-800';
                    };
                    
                    return (
                      <button
                        key={strain}
                        onClick={() => setSelectedStrain(strain)}
                        className={`strain-btn ${selectedStrain === strain ? 'active' : ''} ${getStrainColor(strain)}`}
                      >
                        {strain}
                        <span className="strain-count">{count}</span>
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Sort & Actions */}
          <div className="control-section sort-section">
            <div className="sort-container">
              <label className="sort-label">
                <svg viewBox="0 0 24 24" fill="none" className="sort-icon">
                  <path d="M3 4h18M3 8h12M3 12h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                Sort by:
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="featured">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name-asc">Name: A to Z</option>
                <option value="name-desc">Name: Z to A</option>
                <option value="newest">Newest First</option>
              </select>
            </div>
            
            <div className="action-buttons">
              <button
                onClick={resetFilters}
                className="reset-btn"
                disabled={selectedStrain === 'all' && sortBy === 'featured'}
              >
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                Reset
              </button>
              
              <button
                onClick={() => navigate('/products')}
                className="view-all-btn"
              >
                View All Products
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Results Info */}
        <div className="results-info">
          <p className="results-count">
            Showing <span className="results-highlight">{sortedProducts.length}</span> products
            {selectedStrain !== 'all' && (
              <span className="results-filter"> in <strong>{selectedStrain}</strong></span>
            )}
          </p>
          
          {selectedStrain !== 'all' && (
            <button
              onClick={() => setSelectedStrain('all')}
              className="clear-filter-btn"
            >
              Clear Strain Filter
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>

        {/* Products Grid */}
        {isLoading ? (
          <div className="loading-grid">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="product-skeleton">
                <div className="skeleton-image"></div>
                <div className="skeleton-content">
                  <div className="skeleton-line"></div>
                  <div className="skeleton-line short"></div>
                  <div className="skeleton-line shorter"></div>
                </div>
              </div>
            ))}
          </div>
        ) : sortedProducts.length > 0 ? (
          <div className="products-grid">
            {sortedProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M9.663 17h4.674M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" 
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <h2 className="empty-state-title">No products found</h2>
            <p className="empty-state-text">
              {selectedStrain !== 'all' 
                ? `No ${selectedStrain} strains available in this category`
                : 'No products available in this category'
              }
            </p>
            <div className="empty-state-actions">
              <button
                onClick={() => setSelectedStrain('all')}
                className="empty-state-btn primary"
                disabled={selectedStrain === 'all'}
              >
                Show All Strains
              </button>
              <button
                onClick={() => navigate('/categories')}
                className="empty-state-btn secondary"
              >
                Browse Categories
              </button>
            </div>
          </div>
        )}

        {/* Related Categories */}
        {sortedProducts.length > 0 && (
          <div className="related-categories">
            <h3 className="related-title">Explore Related Categories</h3>
            <div className="related-grid">
              {Object.entries({
                'Edibles': 'Discover delicious THC-infused treats',
                'Concentrates': 'Explore high-potency extracts',
                'Pre-rolls': 'Try perfectly rolled joints'
              }).map(([cat, desc]) => (
                <Link
                  key={cat}
                  to={`/category/${cat}`}
                  className="related-card"
                >
                  <div className="related-icon">{getCategoryIcon(cat)}</div>
                  <div className="related-content">
                    <h4 className="related-name">{cat}</h4>
                    <p className="related-desc">{desc}</p>
                  </div>
                  <div className="related-arrow">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;